import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import NavBar from "@/components/NavBar";
import SwapPicker from "@/components/SwapPicker";
import api from "@/lib/api";

const VERDICT_CLASS = { green: "verdict-green", yellow: "verdict-yellow", red: "verdict-red" };
const FILTERS = ["all", "green", "yellow", "red"];

export default function Library() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [swapTarget, setSwapTarget] = useState(null);
  const [justSwapped, setJustSwapped] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setItems(r.data));
  }, []);

  const filtered = useMemo(
    () => (filter === "all" ? items : items.filter((p) => p.verdict === filter)),
    [items, filter]
  );

  return (
    <div className="App" data-testid="page-library">
      <NavBar />
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <div className="aa-eyebrow mb-3">Your library</div>
        <h1 className="font-display text-4xl lg:text-5xl">Saved <span className="serif-italic">verdicts</span>.</h1>

        <div className="mt-8 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`library-filter-${f}`}
              className={`aa-pill px-4 py-2 transition-colors duration-300 ${filter === f ? "bg-[var(--aa-text)] text-[var(--aa-bg)] border-[var(--aa-text)]" : "hover:bg-[var(--aa-elevated)]"}`}
            >{f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}</button>
          ))}
        </div>

        {justSwapped && (
          <div className="mt-6 aa-pill verdict-green px-4 py-2 text-sm inline-block" data-testid="library-swap-confirm">
            ✓ Swapped into your protocol.
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="aa-card p-10 mt-8 text-center text-[var(--aa-text-2)]">
            Nothing here yet.{" "}
            <Link to="/analyze" data-testid="library-empty-cta" className="underline underline-offset-4 text-[var(--aa-text)]">Analyze your first product</Link>.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
            {filtered.map((p) => (
              <div key={p.id} data-testid={`library-item-${p.id}`} className="aa-card p-5 flex flex-col">
                <Link to={`/products/${p.id}`} className="block hover:opacity-80">
                  <span className={`aa-pill ${VERDICT_CLASS[p.verdict]}`}>{p.verdict.toUpperCase()}</span>
                  <div className="font-display text-lg mt-3">{p.product_name}</div>
                  <div className="text-sm text-[var(--aa-text-3)] mt-1">{p.category.replace("_", " ")} · {p.phase.replace("_", " ")}</div>
                </Link>
                {(p.verdict === "green" || p.verdict === "yellow") && (
                  <button
                    data-testid={`library-swap-${p.id}`}
                    onClick={() => setSwapTarget(p)}
                    className="aa-cta-secondary mt-4 self-start"
                  >
                    {p.verdict === "green" ? "Swap into protocol →" : "Swap in anyway →"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {swapTarget && (
        <SwapPicker
          product={swapTarget}
          onClose={() => setSwapTarget(null)}
          onSwapped={(slotKey) => { setJustSwapped(slotKey); setSwapTarget(null); }}
        />
      )}
    </div>
  );
}
